import React from 'react'
import clsx from 'clsx'
import Link from 'next/link'

import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import Dot from 'components/Dot/Dot'
import AssessmentExpiredDate from '../components/AssessmentExpiredDate'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { USER_ID_STORAGE_KEY } from 'configs/auth'
import { formatDate as formatDateFns } from 'utils/datetime'
import { GATrackEvent } from 'utils/googleAnalytics'

import type { MyAssessment } from 'types/assessment'
import type { LearningGroupAssessment } from 'types/entities'

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  data: MyAssessment | LearningGroupAssessment
  className?: string
  showLearningGroup?: boolean
}

export default function Card({
  data,
  className,
  showLearningGroup,
  ...props
}: CardProps) {
  const [userId] = useLocalStorage(USER_ID_STORAGE_KEY, null)
  const href =
    data.sharedBy === 'online'
      ? `/assessment/me/${data.id}`
      : `/assessment/litnum/${data.questionsPacketId}`

  const totalStudentNotScored =
    data.totalStudentSubmitted - data.totalStudentScored
  const isAssessmentExpired = React.useMemo(
    () => new Date() > new Date(data?.maxSubmission),
    [data?.maxSubmission]
  )

  const trackSavedAssessmentClicked = () => {
    if (userId) {
      const hasBeenUsed =
        data.sharedBy === 'offline' || data.totalStudentSubmitted > 0
      GATrackEvent({
        eventName: hasBeenUsed
          ? 'click_assessment_results'
          : 'click_assessment_details',
        eventParams: {
          event_trigger: 'SAVED_ASSESSMENTS',
          user_id: userId,
          assessment_id: data.questionsPacketId,
          assessment_distribution_type: data.sharedBy,
          ...(hasBeenUsed ? { class_id: data.learningGroupId } : {}),
        },
      })
    }
  }

  return (
    <Link href={href} passHref>
      <div
        {...props}
        className={clsx(
          'bg-white rounded-md shadow hover:shadow-lg cursor-pointer transition-shadow focus:bg-[#EDEDED] focus:outline-none focus:shadow-none',
          className
        )}
        tabIndex={0}
        onClick={() => trackSavedAssessmentClicked()}
      >
        <div className="flex p-4 pb-3">
          <img
            src={data.iconUrl}
            alt={data.name}
            className="rounded-md w-9 h-9"
          />
          <div className="ml-3 flex-1">
            <p className="text-base font-semibold leading-[22px] mb-1">
              {data.name}
            </p>
            <p className="text-sm text-type-basic">
              {data.phase} • {data.subject}
            </p>
          </div>
        </div>
        {data.sharedBy === 'online' && (
          <>
            <hr className="border-[#EDEDED] mb-3" />
            <div className="flex items-start px-4">
              {showLearningGroup && (
                <p className="text-sm flex-1 font-semibold truncate">
                  {data.learningGroup}
                </p>
              )}
              {data.totalStudent > 0 && (
                <>
                  {data.totalStudentSubmitted === data.totalStudent ? (
                    <p className="text-green-500 text-sm font-semibold">
                      Selesai
                    </p>
                  ) : (
                    <p className="text-sm text-right">
                      <span className="text-sm font-semibold">
                        {data.totalStudentSubmitted}
                      </span>{' '}
                      <span className="text-xs text-type-basic-secondary">
                        / {data.totalStudent} Mengumpulkan
                      </span>
                    </p>
                  )}
                </>
              )}
              {data.totalStudent === 0 && (
                <p className="text-sm text-right">
                  <span className="text-sm font-semibold">
                    {data.totalStudentSubmitted}
                  </span>{' '}
                  <span className="text-xs text-type-basic-secondary">
                    Mengumpulkan
                  </span>
                </p>
              )}
            </div>
            {totalStudentNotScored > 0 && isAssessmentExpired && (
              <div className="px-4">
                <div className="bg-surface-informational-subdued p-4 rounded mt-3 flex items-center justify-between">
                  <div className="flex items-center">
                    <Dot />
                    <Text>
                      {totalStudentNotScored} uraian murid belum diperiksa
                    </Text>
                  </div>
                  <Icon>chevron_right</Icon>
                </div>
              </div>
            )}

            <div className="p-4 pt-3">
              <AssessmentExpiredDate date={data.maxSubmission} showHelperText />
            </div>
          </>
        )}
        {data.sharedBy === 'offline' && (
          <div className="p-4 pt-3">
            <Text color="subdued" variant="helper">
              Diunduh pada {formatDateFns(data.lastSharedAt, 'dd MMM • HH:mm')}{' '}
              WIB
            </Text>
          </div>
        )}
      </div>
    </Link>
  )
}
